"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { languageOptions } from "@/config/language-options";
import { LanguageOptionLabel } from "@/components/shared/LanguageFlag";
import useDarkmode from "@/hooks/useDarkmode";
import useLanguage from "@/hooks/useLanguage";
import {
	ChevronDownIcon,
	LanguagesIcon,
	LogOutIcon,
	MoonIcon,
	ShieldCheckIcon,
	SunIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import cacheKeys from "@/config/cache-keys";
import environment from "@/config/environment";
import AppService from "@/services/AppService";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";

const service = new AppService();

export default function Navbar() {
	const navigate = useNavigate();
	const { isDark } = useDarkmode();
	const { t } = useTranslation();

	return (
		<nav className="h-16 border-b px-6 sticky top-0 z-50 bg-background flex items-center dark:bg-[#121212]">
			{/* Left side: Logo */}
			<Tooltip>
				<TooltipTrigger asChild>
					<img
						className="h-14 w-auto cursor-pointer"
						src={
							isDark
								? "/assets/logo/logo-white.svg"
								: "/assets/logo/logo-moss-green.svg"
						}
						alt="Howden Logo"
						onClick={() => {
							window.scrollTo(0, 0);
							navigate("/admin");
						}}
					/>
				</TooltipTrigger>
				<TooltipContent>{t("navbar.dashboard")}</TooltipContent>
			</Tooltip>

			{/* Right side: Theme and User dropdown */}
			<div className="flex items-center gap-2 ml-auto">
				<ThemeToggle />
				<LanguageSelect />
				<UserDropdown />
			</div>
		</nav>
	);
}

const ThemeToggle = () => {
	const { isDark, toggleIsDark } = useDarkmode();
	const { t } = useTranslation();

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button size="icon" variant="ghost" onClick={toggleIsDark}>
					{isDark ? <SunIcon /> : <MoonIcon />}
				</Button>
			</TooltipTrigger>
			<TooltipContent>
				{t("navbar.themetoggle")}{" "}
				{isDark ? t("navbar.lightmode") : t("navbar.darkmode")}
			</TooltipContent>
		</Tooltip>
	);
};

function UserDropdown() {
	const { pathname } = useLocation();

	const auth = useQuery({
		queryKey: cacheKeys.AUTH,
		queryFn: service.getAuth,
		enabled: pathname.includes("/admin"),
		retry: 1,
	});

	const { t } = useTranslation();

	function handleLogout() {
		window.location.href = new URL(
			"/v1/admin/auth/logout",
			environment.get("VITE_BASE_API_URL")
		).toString();
	}

	function handleUserManagement() {
		window.location.href =
			"https://manage.auth0.com/dashboard/eu/dev-b5cc1w4b4ezw7kkl/users";
	}

	if (auth.isLoading) {
		return <div>Loading...</div>;
	}

	if (auth.isError) {
		return;
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				{/* Wrap multiple children in a single container */}
				<div className="flex items-center">
					<Button
						variant="ghost"
						className="justify-start items-center px-2 py-1 rounded-md cursor-pointer"
					>
						<Avatar className="h-8 w-8">
							<AvatarImage
								src={auth.data?.Result.picture.toString()}
								alt="User avatar"
							/>
							<AvatarFallback>
								{auth.data?.Result.nickname[0].toUpperCase()}
							</AvatarFallback>
						</Avatar>
						<div className="flex items-start flex-col leading-tight">
							<span className="font-bold text-sm">
								{auth.data?.Result.nickname}
							</span>
							<span className="text-xs text-gray-500">
								{auth.data?.Result.name}
							</span>
						</div>
						<ChevronDownIcon className="h-5 w-5 ml-2 text-gray-600" />
					</Button>
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent>
				<DropdownMenuItem onClick={handleUserManagement}>
					Manage Users
					<ShieldCheckIcon className="mr-2 h-4 w-4" />
				</DropdownMenuItem>
				<DropdownMenuItem onClick={handleLogout}>
					{t("navbar.logout")}
					<LogOutIcon className="mr-2 h-4 w-4" />
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function LanguageSelect() {
	const { currentLanguage, setLanguage } = useLanguage();
	const { t } = useTranslation();

	return (
		<DropdownMenu>
			<Tooltip>
				<TooltipTrigger asChild>
					<DropdownMenuTrigger asChild>
						<Button size="icon" variant="ghost">
							<LanguagesIcon />
						</Button>
					</DropdownMenuTrigger>
				</TooltipTrigger>
				<TooltipContent>{t("navbar.change-language")}</TooltipContent>
			</Tooltip>
			<DropdownMenuContent className="min-w-[10rem]">
				<DropdownMenuLabel>{t("navbar.choose-language")}</DropdownMenuLabel>
				{languageOptions.map((option) => (
					<DropdownMenuCheckboxItem
						key={option.value}
						checked={currentLanguage === option.value}
						onCheckedChange={() => setLanguage(option.value)}
					>
						<LanguageOptionLabel
							language={option.value}
							label={option.iso6392}
						/>
					</DropdownMenuCheckboxItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
